import React, { useEffect, useState } from 'react';
import { ShowMetaDataType } from '../types/Show';
import { DropArea } from './DropArea';
import { fetchShowMetadata, updateShowMetadata } from '../services/showService';
import { fileUploadService } from '../services/fileUploadService';
import { LoadingScreen } from './LoadingScreen';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Upload } from 'lucide-react';

// Constants for bonus content thumbnails
const SERIES_END_THUMBNAIL_WIDTH = 800;
const SERIES_END_THUMBNAIL_ASPECT_RATIO = 2/3;

interface BonusContentScreenProps {
  showId: string;
  userCanEdit: boolean;
}

interface ThumbnailWithSequence {
  url: string;
  sequence: number;
}

const BonusContentScreen: React.FC<BonusContentScreenProps> = ({ showId, userCanEdit }) => {
  const [loading, setLoading] = useState(true);
  const [seriesFinaleThumbnails, setSeriesFinaleThumbnails] = useState<ThumbnailWithSequence[]>([]);
  const [endCreditsPanels, setEndCreditsPanels] = useState<ThumbnailWithSequence[]>([]);

  const loadAssetsForShow = async (showLoadingState = true) => {
    try {
      if (showLoadingState) {
        setLoading(true);
      }
      
      // Use the correct API pattern from showService
      const showResponse = await fetchShowMetadata(showId, [
        ShowMetaDataType.POST_CREDIT_THUMBNAILS,
        ShowMetaDataType.END_CREDIT_PANELS
      ]);

      console.log('Show response:', showResponse); // Debug log

      // Extract metadata from the show object
      const metadata = showResponse.metadata || [];
      
      const postCreditThumbnails = metadata.find(
        item => item.type === ShowMetaDataType.POST_CREDIT_THUMBNAILS
      );
      const endCreditsData = metadata.find(
        item => item.type === ShowMetaDataType.END_CREDIT_PANELS
      );

      console.log('Post credit thumbnails:', postCreditThumbnails); // Debug log
      console.log('End credits data:', endCreditsData); // Debug log

      const postCreditData = JSON.parse(postCreditThumbnails?.value ?? '[]');
      const endCreditsDataParsed = JSON.parse(endCreditsData?.value ?? '[]');

      console.log('Parsed post credit data:', postCreditData); // Debug log
      console.log('Parsed end credits data:', endCreditsDataParsed); // Debug log

      setSeriesFinaleThumbnails(postCreditData);
      setEndCreditsPanels(endCreditsDataParsed);

      if (showLoadingState) {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading assets:', error);
      if (showLoadingState) {
        setLoading(false);
      }
    }
  };

  const updateAssets = async (type: ShowMetaDataType, thumbnails: ThumbnailWithSequence[]) => {
    try {
      setLoading(true);
      await updateShowMetadata(showId, [{ type, value: JSON.stringify(thumbnails) }]);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const deleteAssets = async (type: ShowMetaDataType, sequences: number[]) => {
    try {
      setLoading(true);
      await updateShowMetadata(showId, [{
        type, 
        value: JSON.stringify(sequences.map(it => {
          return { url: '', sequence: it };
        }))
      }]);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const handleFileRemove = async (type: ShowMetaDataType, sequences: number[]) => {
    if (!userCanEdit) {
      return;
    }
    await deleteAssets(type, sequences);
  };

  const handleFileUpload = async (file: File, type: ShowMetaDataType, sequence: number) => {
    if (!userCanEdit) {
      return;
    }

    if (!file) {
      await deleteAssets(type, [sequence]);
      await loadAssetsForShow();
      return;
    }

    setLoading(true);
    
    try {
      // Validate file first
      const validation = fileUploadService.validateThumbnailFile(file);
      if (!validation.valid) {
        console.error('File validation failed:', validation.error);
        return;
      }

      // Try Azure upload first with proper content type
      let fileUrl: string;
      try {
        fileUrl = await fileUploadService.uploadBonusContentFile(file, showId);
        console.log('Successfully uploaded to Azure:', fileUrl);
      } catch (azureError) {
        console.warn('Azure upload failed, falling back to base64:', azureError);
        // Fallback to base64 if Azure upload fails
        fileUrl = await fileUploadService.uploadThumbnailImage(file);
      }

      // Update metadata with the file URL or base64
      await updateShowMetadata(showId, [{ 
        type, 
        value: JSON.stringify([{ 
          url: fileUrl, 
          sequence: sequence 
        }])
      }]);
      
      await loadAssetsForShow(false);
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkFileUpload = async (files: File[], type: ShowMetaDataType, prevMaxSequence: number) => {
    if (!userCanEdit) {
      return;
    }

    setLoading(true);

    try {
      if (files.length === 0) {
        await updateShowMetadata(showId, [{
          type, 
          value: JSON.stringify(Array.from({ length: prevMaxSequence + 1 }, (_, key) => ({ url: '', sequence: key })))
        }]);
        await loadAssetsForShow(false);
        return;
      }

      // Process files into thumbnails with sequence
      const thumbnails: ThumbnailWithSequence[] = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Validate each file
        const validation = fileUploadService.validateThumbnailFile(file);
        if (!validation.valid) {
          console.error(`File validation failed for ${file.name}:`, validation.error);
          continue; // Skip invalid files
        }
        
        // Try Azure upload first with proper content type
        let fileUrl: string;
        try {
          fileUrl = await fileUploadService.uploadBonusContentFile(file, showId);
          console.log(`Successfully uploaded ${file.name} to Azure:`, fileUrl);
        } catch (azureError) {
          console.warn(`Azure upload failed for ${file.name}, falling back to base64:`, azureError);
          // Fallback to base64 if Azure upload fails
          fileUrl = await fileUploadService.uploadThumbnailImage(file);
        }
        
        thumbnails.push({
          url: fileUrl,
          sequence: i + 1,
        });
      }

      await updateShowMetadata(showId, [{ type, value: JSON.stringify(thumbnails) }]);
      await loadAssetsForShow(false);
    } catch (error) {
      console.error('Error in bulk upload:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssetsForShow();
  }, []);

  return (
    <div className="space-y-8">
      {/* Post Credit Thumbnails Section */}
      <div>
        <h3 className="text-lg font-semibold mb-6">Post Credit Thumbnails</h3>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
            {[...Array(4)].map((_, index) => (
              <div key={index} className="space-y-3">
                <Label className="text-sm font-medium">Post Credit Thumbnail {index + 1}</Label>
                <DropArea
                  imageUrl={(() => {
                    const thumbnail = seriesFinaleThumbnails.find(
                      item => item.sequence === index + 1,
                    );
                    if (!thumbnail?.url) return undefined;
                    
                    // Check if it's already a full URL (Azure blob) or base64 data
                    if (thumbnail.url.startsWith('http') || thumbnail.url.startsWith('https')) {
                      return thumbnail.url; // Azure blob URL
                    } else {
                      return `data:image/jpeg;base64,${thumbnail.url}`; // Base64 data
                    }
                  })()}
                  enforceWidth={false}
                  enforceHeight={false}
                  requiredWidth={SERIES_END_THUMBNAIL_WIDTH}
                  multiple={false}
                  requiredHeight={SERIES_END_THUMBNAIL_WIDTH / SERIES_END_THUMBNAIL_ASPECT_RATIO}
                  accept=".jpg,.png,.jpeg,.webp"
                  label={`Upload or drag and drop\n${SERIES_END_THUMBNAIL_WIDTH}px x ${SERIES_END_THUMBNAIL_WIDTH / SERIES_END_THUMBNAIL_ASPECT_RATIO}px\n.png, .jpg or .jpeg formats`}
                  onFileUpload={file =>
                    handleFileUpload(file, ShowMetaDataType.POST_CREDIT_THUMBNAILS, index + 1)
                  }
                  onFileDelete={() => handleFileRemove(ShowMetaDataType.POST_CREDIT_THUMBNAILS, [index + 1])}
                  sizeScale={4}
                  padding="0px"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* End Credits Panels Section */}
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold mb-4">End Credits Panels</h3>
          
          {/* No panels uploaded state */}
          {endCreditsPanels.length === 0 ? (
            <div className="text-sm text-muted-foreground mb-6">No end credits panels uploaded yet.</div>
          ) : (
            <div className="flex flex-wrap gap-4 mb-6">
              {endCreditsPanels.map((page, index) => (
                <div key={index} className="relative">
                  <img
                    src={(() => {
                      // Check if it's already a full URL (Azure blob) or base64 data
                      if (page.url.startsWith('http') || page.url.startsWith('https')) {
                        return page.url; // Azure blob URL
                      } else {
                        return `data:image/jpeg;base64,${page.url}`; // Base64 data
                      }
                    })()}
                    alt={`Page ${page.sequence}`}
                    className="w-[120px] h-[180px] bg-secondary border rounded-lg object-cover"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Delete All Button */}
          {userCanEdit && endCreditsPanels.length > 0 && (
            <Button
              variant="destructive"
              onClick={async () => {
                await handleFileRemove(
                  ShowMetaDataType.END_CREDIT_PANELS,
                  Array(endCreditsPanels.length).fill(0).map((_, index) => index + 1),
                );
                setEndCreditsPanels([]);
              }}
              className="mb-6"
            >
              Delete All End Credits Panels
            </Button>
          )}

          {/* Bulk Upload Section */}
          <div className="space-y-4">
            <h4 className="text-base font-medium">Bulk Upload End Credits Panels</h4>
            <div 
              className="border-2 border-dashed rounded-lg p-6 text-center transition-colors border-border hover:bg-secondary/20 hover:border-primary/50 cursor-pointer"
              onClick={() => document.getElementById('bulk-upload-input')?.click()}
            >
              <div className="mx-auto w-full max-w-sm space-y-3">
                <div className="flex items-center justify-center gap-3">
                  <Upload className="h-5 w-5 text-muted-foreground/70" />
                  <p className="text-sm text-muted-foreground/70 font-medium">
                    Drag and drop or click to upload panels
                  </p>
                </div>
                <p className="text-xs text-muted-foreground/80">
                  Upload multiple panel files - 800px width .png format recommended
                </p>
                <input
                  id="bulk-upload-input"
                  type="file"
                  multiple
                  accept=".jpg,.png,.jpeg"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files) {
                      handleBulkFileUpload(Array.from(e.target.files), ShowMetaDataType.END_CREDIT_PANELS, endCreditsPanels.length);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {loading && <LoadingScreen />}
    </div>
  );
};

export default BonusContentScreen;